import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { Store } from '@ngrx/store';
import { LocalizationScopeDirective, LocalizePipe } from '@capital-access/common/localization';
import { NotificationsDrawerActions } from '../../+store/notifications-drawer.actions';
import { selectExpandedSections } from '../../+store/notifications-drawer.selectors';
import { NotificationTypePageState } from '../../+store/notifications-drawer.state';
import {
  NOTIFICATION_TYPE_CONFIGS,
  NOTIFICATION_TYPE_LABEL_KEYS,
  NOTIFICATIONS_LOCALIZATION_SCOPE
} from '../../constants/notifications-drawer.constants';
import { NotificationByTypeDto } from '../../models/notifications-grouped.model';
import { NotificationItemRendererComponent } from '../notification-item-renderer/notification-item-renderer.component';

@Component({
  selector: 'ca-notification-type-list',
  templateUrl: './notification-type-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [LocalizationScopeDirective, LocalizePipe, NotificationItemRendererComponent]
})
export class NotificationTypeListComponent {
  private store = inject(Store);

  notificationsByType = input.required<NotificationByTypeDto>();
  typePageState = input<NotificationTypePageState | undefined>();

  loadMore = output<string>();
  markAsRead = output<{ notificationId: number; typeCode: string }>();
  delete = output<{ notificationId: number; typeCode: string }>();

  readonly localizationScope = NOTIFICATIONS_LOCALIZATION_SCOPE;

  private readonly expandedSectionsMap = this.store.selectSignal(selectExpandedSections);

  readonly isExpanded = computed(() => !!this.expandedSectionsMap()[this.notificationsByType().notificationTypeCode]);

  readonly typeLabelKey = computed(
    () =>
      NOTIFICATION_TYPE_LABEL_KEYS[this.notificationsByType().notificationTypeCode] ??
      this.notificationsByType().notificationTypeCode
  );

  readonly initialLimit = computed(
    () => NOTIFICATION_TYPE_CONFIGS[this.notificationsByType().notificationTypeCode]?.initialLimit ?? 3
  );

  readonly displayedNotifications = computed(() =>
    this.isExpanded()
      ? this.notificationsByType().notifications
      : this.notificationsByType().notifications.slice(0, this.initialLimit())
  );

  readonly canToggle = computed(() => this.notificationsByType().totalCount > this.initialLimit());

  readonly hasMore = computed(
    () => this.notificationsByType().totalCount > this.notificationsByType().notifications.length
  );

  readonly isLoadingMore = computed(() => this.typePageState()?.loadingMore ?? false);

  toggleExpanded(): void {
    this.store.dispatch(
      NotificationsDrawerActions.toggleSectionExpansion({
        typeCode: this.notificationsByType().notificationTypeCode
      })
    );
  }

  onShowMore(): void {
    const typeCode = this.notificationsByType().notificationTypeCode;
    // Only expand (set true) on the first click; never toggle back to false here.
    if (!this.isExpanded()) {
      this.store.dispatch(NotificationsDrawerActions.toggleSectionExpansion({ typeCode }));
    }
    if (this.hasMore()) {
      this.loadMore.emit(typeCode);
    }
  }

  onShowLess(): void {
    this.store.dispatch(
      NotificationsDrawerActions.collapseSectionExpansion({
        typeCode: this.notificationsByType().notificationTypeCode
      })
    );
  }

  onMarkAsRead(notificationId: number): void {
    this.markAsRead.emit({
      notificationId,
      typeCode: this.notificationsByType().notificationTypeCode
    });
  }

  onDelete(notificationId: number): void {
    this.delete.emit({
      notificationId,
      typeCode: this.notificationsByType().notificationTypeCode
    });
  }
}
